/* Hallmark · component: prerequisite-picker · genre: editorial · theme: Operational Architect
 * states: default · hover · focus · active · disabled · loading · error · success
 * pre-emit critique: P4 H4 E4 S5 R5 V4 · uses existing theme tokens */
import { useId, useState } from 'react';
import { Search, CheckCircle2, X } from 'lucide-react';
import type { ProgramLevel, ProgramSubject } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';

interface SubjectPrerequisitePickerProps {
  enabled: boolean;
  value: string;
  subjects: ProgramSubject[];
  levels?: ProgramLevel[];
  disabled?: boolean;
  error?: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onChange: (id: string) => void;
}

export function SubjectPrerequisitePicker({
  enabled,
  value,
  subjects,
  levels = [],
  disabled = false,
  error = false,
  onEnabledChange,
  onChange,
}: SubjectPrerequisitePickerProps) {
  const { t } = useLanguage();
  const id = useId();
  const [search, setSearch] = useState('');
  const locations = new Map(
    levels.flatMap(level =>
      (level.stages ?? []).flatMap(stage =>
        (stage.subjects ?? []).map(
          subject => [subject.id, `${level.name} · ${stage.name}`] as const
        )
      )
    )
  );
  const selected = subjects.find(subject => subject.id === value);
  const query = search.trim().toLocaleLowerCase();
  const matches = subjects.filter(subject =>
    [subject.name, subject.code, locations.get(subject.id)]
      .join(' ')
      .toLocaleLowerCase()
      .includes(query)
  );
  const choiceClass =
    'flex min-h-12 cursor-pointer items-center gap-3 rounded-xl p-3 text-sm text-on-surface hover:bg-surface-container-high has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 active:bg-surface-container-highest';

  return (
    <fieldset className="min-w-0 space-y-3" disabled={disabled} aria-describedby={`${id}-help`}>
      <legend className="mb-2 text-sm font-bold text-on-surface">
        {t(copy.subjectModal.labelPrerequisite)}
      </legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {[false, true].map(required => (
          <label
            key={String(required)}
            className={`${choiceClass} ${enabled === required ? 'bg-primary/10' : 'bg-surface-container-low'}`}
          >
            <input
              type="radio"
              name={`${id}-mode`}
              checked={enabled === required}
              onChange={() => onEnabledChange(required)}
              className="h-5 w-5 shrink-0 accent-primary"
            />
            <span className="font-semibold">
              {t(
                required ? copy.subjectModal.choosePrerequisite : copy.subjectModal.nonePrerequisite
              )}
            </span>
          </label>
        ))}
      </div>
      <p id={`${id}-help`} className="m-0 text-sm leading-relaxed text-on-surface-variant">
        {t(enabled ? copy.subjectModal.prerequisiteHelp : copy.subjectModal.noPrerequisiteHelp)}
      </p>
      {enabled && (
        <div className="space-y-3">
          {value && (
            <div
              role="status"
              className="rounded-xl bg-success-container p-3 text-sm text-on-surface"
            >
              <p className="m-0 flex items-center gap-2 font-semibold">
                <CheckCircle2 size={18} aria-hidden="true" />
                {t(copy.subjectModal.selectedPrerequisite)}
              </p>
              <p className="mb-0 mt-1 break-words">
                {selected
                  ? `${selected.name}${selected.code ? ` (${selected.code})` : ''}`
                  : t(copy.subjectModal.prerequisiteUnavailable)}
              </p>
              {locations.get(value) && (
                <p className="mb-0 mt-1 text-on-surface-variant">{locations.get(value)}</p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <label htmlFor={`${id}-search`} className="block text-sm font-semibold text-on-surface">
              {t(copy.subjectModal.searchPrerequisite)}
            </label>
            <div className="relative">
              <Search
                size={18}
                aria-hidden="true"
                className="pointer-events-none absolute start-3 top-4 text-on-surface-variant"
              />
              <input
                id={`${id}-search`}
                type="search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder={t(copy.subjectModal.searchPrerequisitePlaceholder)}
                className="min-h-12 w-full rounded-xl bg-surface-container-high ps-10 pe-12 text-base text-on-surface placeholder:text-on-surface-variant focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              />
              {search && (
                <button
                  type="button"
                  aria-label={t(copy.subjectModal.clearPrerequisiteSearch)}
                  onClick={() => setSearch('')}
                  className="absolute end-0 top-0 flex h-12 w-12 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container-highest active:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
          {error && (
            <p
              id={`${id}-error`}
              role="alert"
              className="m-0 rounded-xl bg-error-container p-3 text-sm text-on-error-container"
            >
              {t(copy.subjectModal.prerequisiteRequired)}
            </p>
          )}
          <fieldset
            className="max-h-56 min-w-0 space-y-2 overflow-y-auto rounded-xl bg-surface-container-low p-2"
            aria-invalid={error}
            aria-describedby={error ? `${id}-error` : undefined}
          >
            <legend className="sr-only">{t(copy.subjectModal.choosePrerequisite)}</legend>
            {matches.map(subject => (
              <label
                key={subject.id}
                className={`${choiceClass} ${value === subject.id ? 'bg-primary/10' : 'bg-surface-container-lowest'}`}
              >
                <input
                  type="radio"
                  name={`${id}-subject`}
                  value={subject.id}
                  checked={value === subject.id}
                  onChange={() => onChange(subject.id)}
                  className="h-5 w-5 shrink-0 accent-primary"
                />
                <span className="min-w-0 break-words">
                  <span className="block font-semibold">
                    {subject.name}
                    {subject.code ? ` (${subject.code})` : ''}
                  </span>
                  {locations.get(subject.id) && (
                    <span className="mt-1 block text-on-surface-variant">
                      {locations.get(subject.id)}
                    </span>
                  )}
                </span>
              </label>
            ))}
            {!matches.length && (
              <p role="status" className="m-0 p-3 text-sm leading-relaxed text-on-surface-variant">
                {t(
                  subjects.length
                    ? copy.subjectModal.prerequisiteNotFound
                    : copy.subjectModal.prerequisiteEmpty
                )}
              </p>
            )}
          </fieldset>
        </div>
      )}
    </fieldset>
  );
}
